import * as THREE from 'three';
import { createSlimeMaterial, type SlimeStyle } from '../character/Slime';

const TETHER_RADIUS = 0.09;
/** Segmen + cincin tube tetap. Geometri dibuat SEKALI lalu posisinya
 *  diupdate per frame — tanpa dispose+alokasi tiap frame (sumber churn
 *  GPU yang bisa memicu WebGL context loss di Firefox).
 *  RINGS 10 -> cross-section bulat mulus (bukan segi enam berfaset). */
const TUBE_SEGMENTS = 16;
const TUBE_RINGS = 10;

const _noNormal = new THREE.Vector3(0, 0, 1);
const _padQ = new THREE.Quaternion();
const _padZ = new THREE.Vector3(0, 0, 1);
const _tangent = new THREE.Vector3();
const _n1 = new THREE.Vector3();
const _n2 = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _p = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _rest1 = new THREE.Vector3();
const _rest2 = new THREE.Vector3();
const _acc = new THREE.Vector3();

/**
 * Tali karet slime penghubung slime <-> crate yang di-grab.
 * Material goo SAMA dengan badan slime (warna + shading identik).
 * Elastisitas visual mengikuti regangan: makin tegang (strain tinggi)
 * makin lurus dan menipis; kendur -> melorot (sag) dan tebal.
 * Karena pendek (1-2,5 m) dan melengkung, terbaca dari semua sudut.
 *
 * - Ujung slime: TANPA objek apa pun (tanpa bola, tanpa orb/weld).
 *   Tube mulai sedikit di dalam kulit (titik live dari pemanggil);
 *   segmen interior gugur uji depth (renderOrder, lihat bawah) sehingga
 *   tali terlihat keluar bersih dari goo, tanpa sambungan terlihat dan
 *   tanpa jejak di dalam slime transparan.
 * - Ujung crate: tempelan pipih (goo splat) yang duduk dalam di
 *   permukaan, statis mengikuti crate (titiknya dikunci pemanggil saat
 *   grab mulai, bukan dihitung ulang tiap frame).
 */
export class SlimeRope {
  private tube: THREE.Mesh;
  private pad: THREE.Mesh;
  private marker: THREE.Mesh;
  private goo = createSlimeMaterial();
  /** Titik kontrol kurva cubic + spring-damper (secondary motion).
   *  Daripada langsung lerp, mid-points ini mengejar posisi istirahatnya
   *  dengan pegas underdamped -> lentur/jiggle khas slime. */
  private c1 = new THREE.Vector3();
  private c2 = new THREE.Vector3();
  private v1 = new THREE.Vector3();
  private v2 = new THREE.Vector3();
  private springInit = false;

  constructor(private scene: THREE.Scene) {
    // renderOrder 2 = SETELAH badan slime (renderOrder 1) dengan depth-test:
    // fragmen tali/pad yang berada DI DALAM badan (atau crate) gagal uji
    // depth terhadap dinding yang sudah ditulis badan duluan -> tali tak
    // pernah tampak menembus slime transparan dari sudut mana pun.
    // Yang di luar siluet tetap tampil normal. Trik yang sama dipakai
    // trail (SlimeTrail).
    // Geometri tube persisten: tanpa disposal + alokasi tiap frame.
    const tubeGeo = new THREE.BufferGeometry();
    const vertCount = (TUBE_SEGMENTS + 1) * TUBE_RINGS;
    const tubePos = new Float32Array(vertCount * 3);
    tubeGeo.setAttribute('position', new THREE.BufferAttribute(tubePos, 3));
    const idx: number[] = [];
    for (let s = 0; s < TUBE_SEGMENTS; s++) {
      for (let r = 0; r < TUBE_RINGS; r++) {
        const a = s * TUBE_RINGS + r;
        const a1 = s * TUBE_RINGS + ((r + 1) % TUBE_RINGS);
        const b = (s + 1) * TUBE_RINGS + r;
        const b1 = (s + 1) * TUBE_RINGS + ((r + 1) % TUBE_RINGS);
        idx.push(a, a1, b);
        idx.push(a1, b1, b);
      }
    }
    tubeGeo.setIndex(idx);
    tubeGeo.computeVertexNormals();
    this.tube = new THREE.Mesh(tubeGeo, this.goo.material);
    this.tube.name = 'SlimeRopeTube';
    this.tube.frustumCulled = false;
    this.tube.visible = false;
    this.tube.castShadow = true;
    this.tube.renderOrder = 2;
    scene.add(this.tube);

    // Tempelan pipih di permukaan crate: bola dipipihkan sepanjang normal
    // permukaan + sedikit terbenam, jadi terlihat menyatu dan statis.
    this.pad = new THREE.Mesh(new THREE.SphereGeometry(1, 14, 12), this.goo.material);
    this.pad.frustumCulled = false;
    this.pad.visible = false;
    this.pad.castShadow = true;
    this.pad.renderOrder = 2;
    scene.add(this.pad);

    this.marker = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.06, 10, 32),
      new THREE.MeshStandardMaterial({
        color: 0x5dffd4,
        emissive: 0x5dffd4,
        emissiveIntensity: 1.2,
        roughness: 0.3,
      }),
    );
    this.marker.frustumCulled = false;
    this.marker.visible = false;
    scene.add(this.marker);
  }

  setStyle(style: SlimeStyle): void {
    this.goo.setStyle(style);
  }

  /** Warna goo tali mengikuti warna slime pemilik. */
  setColor(hex: number): void {
    this.goo.material.color.setHex(hex);
  }

  /**
   * Tampilkan tali elastis slime -> crate. strain 0 (kendur) .. 1 (tegang).
   * `from` = titik jangkar sedikit di dalam kulit slime (segmen interior
   * gugur uji depth berkat renderOrder; tak ada objek sambungan di sini).
   * `to` titik tempel di permukaan crate + `normal` normal permukaan
   * (orientasi tempelan). Titik `to` dikunci pemanggil.
   * `delta` dipakai untuk integrasi spring titik-titik tengah.
   */
  link(
    from: THREE.Vector3,
    to: THREE.Vector3,
    strain01: number,
    time: number,
    normal?: THREE.Vector3,
    delta = 1 / 60,
  ): void {
    const strain = THREE.MathUtils.clamp(strain01, 0, 1);
    const dt = Math.min(Math.max(delta, 0), 0.05);

    // ---- Kurva cubic Bezier dengan 2 titik kontrol tengah ----
    // Rest pose: dua titik bend di sepertiga & dua pertiga panjang,
    // melorot (sag) sebanding slack + sedikit goyang.
    const len = from.distanceTo(to);
    _dir.subVectors(to, from);
    if (len < 1e-4) _dir.set(0, 0, 1).multiplyScalar(0.3);
    else _dir.multiplyScalar(1 / len);
    const sag = (0.3 * (1 - strain) + 0.03) * Math.min(len / 2, 1.4);
    _rest1.copy(from).addScaledVector(_dir, len * 0.3);
    _rest1.y -= sag * 0.8;
    _rest1.x += Math.sin(time * 5.1) * 0.02 * (1 - strain);
    _rest2.copy(to).addScaledVector(_dir, -len * 0.3);
    _rest2.y -= sag * 0.45;
    _rest2.z += Math.cos(time * 4.3) * 0.02 * (1 - strain);

    // ---- Secondary motion: spring-damper underdamped ----
    if (!this.springInit) {
      this.c1.copy(_rest1);
      this.c2.copy(_rest2);
      this.v1.set(0, 0, 0);
      this.v2.set(0, 0, 0);
      this.springInit = true;
    }
    this.springStep(this.c1, this.v1, _rest1, 130, 8.5, dt);
    this.springStep(this.c2, this.v2, _rest2, 130, 8.5, dt);

    const radius = Math.max(TETHER_RADIUS * (1 - 0.4 * strain), 0.02);
    this.fillTube(from, this.c1, this.c2, to, radius);
    this.tube.visible = true;
    this.marker.visible = false;

    // Tempelan pipih: orientasi presisi via quaternion — sumbu lokal +Z
    // PERSIS sejajar normal permukaan (lookAt bisa miring tergantung up
    // dan selalu diperhitungkan roll; di sini deterministik). Disk
    // dipipihkan di +Z -> selalu rata menempel penuh ke muka crate.
    const n = normal && normal.lengthSq() > 1e-6 ? normal : _noNormal;
    _padQ.setFromUnitVectors(_padZ, n);
    this.pad.quaternion.copy(_padQ);
    this.pad.position.copy(to).addScaledVector(n, -0.05);
    const padR = Math.max(radius, 0.02) * 1.9;
    this.pad.scale.set(padR * 1.6, padR * 1.6, padR * 0.42);
    this.pad.visible = true;
  }

  private springStep(
    cur: THREE.Vector3,
    vel: THREE.Vector3,
    rest: THREE.Vector3,
    k: number,
    d: number,
    dt: number,
  ): void {
    _acc.subVectors(rest, cur).multiplyScalar(k).addScaledVector(vel, -d);
    vel.addScaledVector(_acc, dt);
    const vm = vel.length();
    if (vm > 10) vel.multiplyScalar(10 / vm);
    cur.addScaledVector(vel, dt);
  }

  /** Isi ulang posisi vertex tube (geometri reuse, tanpa alokasi GPU).
   *  Cubic Bezier (p0..p3) + PROFIL RADIUS taper: menebal di kedua ujung
   *  (menyatu dengan body & pad) dan menipis di tengah — bentuk cairan
   *  kental yang ditarik, bukan tabung seragam. */
  private fillTube(
    p0: THREE.Vector3,
    p1: THREE.Vector3,
    p2: THREE.Vector3,
    p3: THREE.Vector3,
    radius: number,
  ): void {
    const geo = this.tube.geometry as THREE.BufferGeometry;
    const pos = geo.attributes.position as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    const seg = TUBE_SEGMENTS;
    const rings = TUBE_RINGS;
    let o = 0;
    for (let s = 0; s <= seg; s++) {
      const t = s / seg;
      const mt = 1 - t;
      // titik pada cubic bezier
      _p.set(0, 0, 0).addScaledVector(p0, mt * mt * mt);
      _p.addScaledVector(p1, 3 * mt * mt * t);
      _p.addScaledVector(p2, 3 * mt * t * t);
      _p.addScaledVector(p3, t * t * t);
      // tangen = turunan
      _tangent.set(0, 0, 0).addScaledVector(p1, 3 * mt * mt);
      _tangent.addScaledVector(p2, 6 * mt * t);
      _tangent.addScaledVector(p3, 3 * t * t);
      _tangent.addScaledVector(p0, -3 * mt * mt);
      _tangent.addScaledVector(p1, -6 * mt * t);
      if (_tangent.lengthSq() < 1e-8) _tangent.copy(_p).sub(p0);
      _tangent.normalize();
      _n1.crossVectors(_up, _tangent);
      if (_n1.lengthSq() < 1e-8) {
        // Tangen sejajar up: basis buatan manual.
        _n1.set(1, 0, 0);
        _n2.set(0, 0, 1);
      } else {
        _n1.normalize();
        _n2.crossVectors(_tangent, _n1).normalize();
      }
      // Profil taper: ujung 1.15x, tengah 0.5x (tetesan ditarik).
      const taper = Math.sin(Math.PI * t);
      const r = radius * (1.15 - 0.65 * taper);
      for (let rn = 0; rn < rings; rn++) {
        const a = (Math.PI * 2 * rn) / rings;
        arr[o++] = _p.x + (_n1.x * Math.cos(a) + _n2.x * Math.sin(a)) * r;
        arr[o++] = _p.y + (_n1.y * Math.cos(a) + _n2.y * Math.sin(a)) * r;
        arr[o++] = _p.z + (_n1.z * Math.cos(a) + _n2.z * Math.sin(a)) * r;
      }
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
  }

  /** Indikator grab: cincin berputar di atas crate kandidat (null = sembunyi). */
  mark(pos: THREE.Vector3 | null, time: number): void {
    if (!pos) {
      this.marker.visible = false;
      return;
    }
    this.marker.position.copy(pos);
    this.marker.position.y += 0.15 * Math.sin(time * 4);
    this.marker.rotation.x = Math.PI / 2 + 0.25 * Math.sin(time * 2);
    this.marker.rotation.z = time * 1.5;
    this.marker.visible = true;
  }

  hide(): void {
    this.tube.visible = false;
    this.pad.visible = false;
    this.marker.visible = false;
  }

  dispose(): void {
    this.scene.remove(this.tube);
    this.scene.remove(this.pad);
    this.scene.remove(this.marker);
    this.tube.geometry.dispose();
    this.pad.geometry.dispose();
    this.marker.geometry.dispose();
    this.goo.material.dispose();
  }
}
